import { createContext, useContext, useState, useEffect, ReactNode, useMemo } from 'react'
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles'
import { PaletteMode } from '@mui/material'

interface ThemeContextType {
  mode: PaletteMode
  toggleTheme: () => void
  setThemeMode: (mode: PaletteMode) => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

const THEME_STORAGE_KEY = 'ConnectLoopTheme'

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<PaletteMode>(() => {
    const stored = localStorage.getItem(THEME_STORAGE_KEY)
    if (stored === 'light' || stored === 'dark') {
      return stored
    }
    // Check system preference
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  })

  useEffect(() => {
    localStorage.setItem(THEME_STORAGE_KEY, mode)
  }, [mode])

  const toggleTheme = () => {
    setMode((prevMode: string) => (prevMode === 'light' ? 'dark' : 'light'))
  }

  const setThemeMode = (newMode: PaletteMode) => {
    setMode(newMode)
  }

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: mode === 'dark'
            ? {
                main: '#4db6ac',
                light: '#80cbc4',
                dark: '#00897b',
                contrastText: '#000000',
              }
            : {
                main: '#006060',
                light: '#338080',
                dark: '#004343',
                contrastText: '#ffffff',
              },
          secondary: {
            main: '#7a1faf',
            light: '#944cbf',
            dark: '#55157a',
            contrastText: '#ffffff',
          },
          text: {
            ...(mode === 'light' && {
              secondary: 'rgba(0, 0, 0, 0.7)',
            }),
          },
          ...(mode === 'dark' && {
            background: {
              default: '#2b2b3d',
              paper: '#32324a',
            },
            text: {
              primary: '#e0e0e8',
              secondary: '#a0a0b8',
            },
          }),
        },
        components: {
          MuiAppBar: {
            defaultProps: {
              enableColorOnDark: true,
            },
            styleOverrides: {
              root: {
                ...(mode === 'dark' && {
                  backgroundColor: '#006060',
                  color: '#ffffff',
                }),
              },
            },
          },
          MuiLink: {
            styleOverrides: {
              root: {
                ...(mode === 'dark' && {
                  color: '#80cbc4',
                }),
              },
            },
          },
          MuiCssBaseline: {
            styleOverrides: {
              ...(mode === 'dark' && {
                a: {
                  color: '#80cbc4',
                  '&:visited': {
                    color: '#ce93d8',
                  },
                },
              }),
            },
          },
        },
      }),
    [mode]
  )

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme, setThemeMode }}>
      <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useTheme() {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}
